import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ProjectGroup, TimesheetEntry } from '../../modules/Timesheets/config';
import { dateKey, dayTotals, entriesFor, isSimpleCell, sumHours, weekDays } from '../../modules/Timesheets/timesheetDerive';
import { formatHoursShort, parseHours, roundToQuarter } from '../../utils/timesheetTime';
import SaveDot, { SaveState } from '../SaveDot';
import IconButton from '../IconButton';
import { CloseIcon, EditIcon } from '../IconButton/icons';
import SearchableProjectDropdown from '../SearchableProjectDropdown';
import type { ProjectOption } from '../SearchableProjectDropdown/config';
import './BaseTimesheetGrid.scss';

export interface BaseTimesheetGridProps {
  /** Rows, already grouped and merged with any blank rows (timesheetDerive.groupEntries). */
  groups: ProjectGroup[];
  weekEndingDate: string;
  isReadOnly?: boolean;
  /** Tones the day-total row: short of this is muted, at or over is normal. */
  standardHoursPerDay?: number | null;
  /**
   * A simple cell was committed: `hours` is the parsed, quarter-rounded value
   * or null when the cell was cleared; `existing` is the entry it replaces.
   */
  onCellCommit?: (group: ProjectGroup, date: string, hours: number | null, existing?: TimesheetEntry) => Promise<void> | void;
  /** A complex cell (several entries, or one with notes or times) was clicked. */
  onOpenCell?: (group: ProjectGroup, date: string, entries: TimesheetEntry[]) => void;
  /** "+" on a complex cell: add another entry for this project and day. */
  onAddToCell?: (group: ProjectGroup, date: string) => void;
  onProjectClick?: (group: ProjectGroup) => void;
  /** Remove a blank row (one with no entries). Renders the row's close button when given. */
  onRemoveBlankRow?: (group: ProjectGroup) => void;
  /**
   * The add row at the bottom: a project search the user types a job number
   * into. Renders the row when given.
   */
  onAddProject?: (option: ProjectOption) => void;
  /** Move focus into this row's first empty cell (after it was just added). */
  focusRowKey?: string | null;
  onRowFocused?: () => void;
  cellSaveState?: (group: ProjectGroup, date: string) => SaveState;
  className?: string;
}

/**
 * The week grid. Editable cells are uncontrolled inputs keyed on the entry
 * id (never on the hours), so a refetch after a save cannot remount a cell
 * the user has already moved on from, and Tab flows along the row.
 */
const BaseTimesheetGrid: React.FC<BaseTimesheetGridProps> = ({
  groups,
  weekEndingDate,
  isReadOnly = false,
  standardHoursPerDay = null,
  onCellCommit,
  onOpenCell,
  onAddToCell,
  onProjectClick,
  onRemoveBlankRow,
  onAddProject,
  focusRowKey = null,
  onRowFocused,
  cellSaveState,
  className = ''
}) => {
  const days = useMemo(() => weekDays(weekEndingDate), [weekEndingDate]);
  const allEntries = useMemo(() => groups.flatMap((g) => g.entries), [groups]);
  const totalsByDay = useMemo(() => dayTotals(allEntries), [allEntries]);
  const grandTotal = useMemo(() => sumHours(allEntries), [allEntries]);
  const editable = !isReadOnly && Boolean(onCellCommit);
  const todayKey = dateKey(new Date());
  const tableRef = useRef<HTMLTableElement>(null);
  // Remounts the add row's search after a pick so it comes back empty.
  const [addKey, setAddKey] = useState(0);

  // A row that was just added gets the keyboard: today's cell if the week
  // holds today, else its first cell.
  useEffect(() => {
    if (!focusRowKey || !tableRef.current) return;
    const row = tableRef.current.querySelector<HTMLTableRowElement>(`tr[data-row-key="${CSS.escape(focusRowKey)}"]`);
    const inputs = row ? Array.from(row.querySelectorAll<HTMLInputElement>('.timesheet-grid__input')) : [];
    const target = inputs.find((i) => i.dataset.date === todayKey) ?? inputs[0];
    target?.focus();
    onRowFocused?.();
  }, [focusRowKey, todayKey, onRowFocused]);

  if (days.length === 0) return null;
  const columnCount = days.length + 3 + (onRemoveBlankRow ? 1 : 0);

  const commitCell = (
    input: HTMLInputElement,
    group: ProjectGroup,
    date: string,
    existing: TimesheetEntry | undefined
  ) => {
    if (!onCellCommit) return;
    const raw = input.value;
    const parsed = parseHours(raw);
    const previous = existing ? Number(existing.hours) : null;

    if (parsed === null) {
      if (raw.trim() !== '' || previous === null) {
        // Unreadable, or blank over nothing: put back what was there.
        input.value = previous === null ? '' : formatHoursShort(previous);
        return;
      }
      onCellCommit(group, date, null, existing);
      return;
    }

    const hours = roundToQuarter(parsed);
    if (hours <= 0) {
      if (previous !== null) onCellCommit(group, date, null, existing);
      else input.value = '';
      return;
    }
    input.value = formatHoursShort(hours);
    if (hours === previous) return;
    onCellCommit(group, date, hours, existing);
  };

  return (
    <div className={`timesheet-grid-outer ${className}`.trim()}>
      <div className="timesheet-grid-wrap">
      <table ref={tableRef} className={`ui-table timesheet-grid ${editable ? 'is-editable' : 'is-readonly'}`}>
        <thead>
          <tr>
            <th className="timesheet-grid__code-col">Job</th>
            <th className="timesheet-grid__name-col">Project</th>
            {days.map((day) => {
              const key = dateKey(day);
              return (
                <th key={key} className={`timesheet-grid__day-col ${key === todayKey ? 'is-today' : ''}`}>
                  <span className="timesheet-grid__day-name">{format(day, 'EEE')}</span>
                  <span className="timesheet-grid__day-date">{format(day, 'd MMM')}</span>
                </th>
              );
            })}
            <th className="timesheet-grid__total-col">Total</th>
            {onRemoveBlankRow && <th className="timesheet-grid__remove-col" />}
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 && !onAddProject && (
            <tr className="timesheet-grid__empty-row">
              <td colSpan={columnCount}>
                <p className="ui-table__empty">No time entries for this week.</p>
              </td>
            </tr>
          )}
          {groups.map((group) => {
            const rowTotal = sumHours(group.entries);
            const clickable = !group.isLeave && Boolean(onProjectClick);
            const isBlank = group.entries.length === 0;
            return (
              <tr
                key={group.groupKey}
                className={`timesheet-grid__row ${group.isLeave ? 'is-leave' : ''} ${isBlank ? 'is-blank' : ''}`}
                data-row-key={group.groupKey}
                data-leave-type={group.leaveType ?? undefined}
              >
                <td className="timesheet-grid__code-col">
                  {group.isLeave ? (
                    <span className="ui-chip">Leave</span>
                  ) : (
                    <span className="ui-table__mono">{group.jobNumber ?? '—'}</span>
                  )}
                </td>
                <td className="timesheet-grid__name-col">
                  {clickable ? (
                    <button type="button" className="timesheet-grid__project-btn" onClick={() => onProjectClick?.(group)}>
                      <span className="timesheet-grid__project-name">{group.projectName}</span>
                      {group.parentDescription && (
                        <span className="timesheet-grid__project-sub">{group.parentDescription}</span>
                      )}
                    </button>
                  ) : (
                    <span className="timesheet-grid__project-static">
                      <span className="timesheet-grid__project-name">{group.projectName}</span>
                      {group.parentDescription && (
                        <span className="timesheet-grid__project-sub">{group.parentDescription}</span>
                      )}
                    </span>
                  )}
                </td>
                {days.map((day) => {
                  const date = dateKey(day);
                  const cellEntries = entriesFor(group, date);
                  const cellHours = sumHours(cellEntries);
                  const simple = isSimpleCell(cellEntries);
                  const existing = cellEntries[0];
                  const saveState = cellSaveState?.(group, date) ?? 'idle';
                  const isToday = date === todayKey;
                  const shown = existing ? formatHoursShort(Number(existing.hours)) : '';

                  if (!editable) {
                    return (
                      <td key={date} className={`timesheet-grid__day-col ${isToday ? 'is-today' : ''}`}>
                        {cellHours > 0 ? (
                          <span className="timesheet-grid__value">{formatHoursShort(cellHours)}</span>
                        ) : (
                          <span className="ui-table__na">–</span>
                        )}
                      </td>
                    );
                  }

                  if (!simple) {
                    return (
                      <td key={date} className={`timesheet-grid__day-col is-complex ${isToday ? 'is-today' : ''}`}>
                        <span className="timesheet-grid__cell">
                        <button
                          type="button"
                          className="timesheet-grid__complex"
                          title={
                            cellEntries.length > 1
                              ? `${cellEntries.length} entries. Open the day to edit them.`
                              : 'This entry has notes or times. Open it to edit.'
                          }
                          onClick={() => onOpenCell?.(group, date, cellEntries)}
                        >
                          <span className="timesheet-grid__value">{formatHoursShort(cellHours)}</span>
                          <span className="timesheet-grid__complex-badge" aria-hidden="true">
                            {cellEntries.length > 1 ? cellEntries.length : <EditIcon size={9} />}
                          </span>
                        </button>
                        {onAddToCell && (
                          <button
                            type="button"
                            className="timesheet-grid__add-btn"
                            tabIndex={-1}
                            title="Add another entry"
                            aria-label={`${group.projectName}, ${format(day, 'EEEE d MMMM')}, add another entry`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onAddToCell(group, date)}
                          >
                            +
                          </button>
                        )}
                        </span>
                      </td>
                    );
                  }

                  return (
                    <td key={date} className={`timesheet-grid__day-col is-input ${isToday ? 'is-today' : ''}`}>
                      <span className="timesheet-grid__cell">
                        <input
                          // Keyed on the entry id, never the hours: a refetch after a
                          // save must not remount a cell mid-edit.
                          key={`${group.groupKey}:${date}:${existing?.id ?? 'new'}`}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          className={`text-input text-input--sm text-input--mono timesheet-grid__input ${saveState === 'error' ? 'is-invalid' : ''}`}
                          defaultValue={shown}
                          // The input is uncontrolled so a refetch cannot rewrite it under
                          // the caret, but an entry edited elsewhere (the editor, the day
                          // view) still has to show up: mirror the saved hours in whenever
                          // the cell is not the one being typed into.
                          ref={(el) => {
                            if (el && document.activeElement !== el && el.value !== shown) el.value = shown;
                          }}
                          placeholder="–"
                          data-date={date}
                          aria-label={`${group.projectName}, ${format(day, 'EEEE d MMMM')}, hours`}
                          onFocus={(e) => e.currentTarget.select()}
                          onBlur={(e) => commitCell(e.currentTarget, group, date, existing)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              e.currentTarget.value = shown;
                              e.currentTarget.blur();
                            }
                          }}
                        />
                        {saveState !== 'idle' && <SaveDot state={saveState} className="timesheet-grid__dot" />}
                        {onOpenCell && (
                          // Notes and clock times live in the editor; this is the way
                          // in from a cell the grid otherwise edits as a bare number.
                          <button
                            type="button"
                            className="timesheet-grid__note-btn"
                            tabIndex={-1}
                            title={existing ? 'Add notes or times' : 'Add an entry with notes or times'}
                            aria-label={`${group.projectName}, ${format(day, 'EEEE d MMMM')}, add notes`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onOpenCell(group, date, cellEntries)}
                          >
                            <EditIcon size={11} />
                          </button>
                        )}
                        {onAddToCell && existing && (
                          // A second entry for the same project and day, with its own
                          // notes (worked on it twice). Once it exists the cell is
                          // "complex" and opens the editor rather than editing inline.
                          <button
                            type="button"
                            className="timesheet-grid__add-btn"
                            tabIndex={-1}
                            title="Add another entry"
                            aria-label={`${group.projectName}, ${format(day, 'EEEE d MMMM')}, add another entry`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onAddToCell(group, date)}
                          >
                            +
                          </button>
                        )}
                      </span>
                    </td>
                  );
                })}
                <td className="timesheet-grid__total-col">
                  <span className="timesheet-grid__value is-total">{rowTotal > 0 ? formatHoursShort(rowTotal) : '–'}</span>
                </td>
                {onRemoveBlankRow && (
                  <td className="timesheet-grid__remove-col">
                    {isBlank && (
                      <IconButton
                        icon={<CloseIcon size={14} />}
                        label="Remove row"
                        size="sm"
                        onClick={() => onRemoveBlankRow(group)}
                      />
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {groups.length > 0 && (
          <tfoot>
            <tr className="timesheet-grid__totals">
              <td colSpan={2} className="timesheet-grid__totals-label">
                Day total
              </td>
              {days.map((day) => {
                const date = dateKey(day);
                const total = totalsByDay.get(date) ?? 0;
                const short = standardHoursPerDay !== null && total > 0 && total < standardHoursPerDay;
                const met = standardHoursPerDay !== null && total >= standardHoursPerDay;
                return (
                  <td
                    key={date}
                    className={`timesheet-grid__day-col ${short ? 'is-short' : ''} ${met ? 'is-met' : ''} ${date === todayKey ? 'is-today' : ''}`}
                  >
                    <span className="timesheet-grid__value is-total">{total > 0 ? formatHoursShort(total) : '–'}</span>
                  </td>
                );
              })}
              <td className="timesheet-grid__total-col">
                <span className="timesheet-grid__value is-grand">{formatHoursShort(grandTotal)}</span>
              </td>
              {onRemoveBlankRow && <td className="timesheet-grid__remove-col" />}
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      {onAddProject && (
        // Outside the scrolling table on purpose: the wrap clips on both axes,
        // and the search's result list must be able to drop below the grid.
        <div className="timesheet-grid__add form-ui">
          <span className="timesheet-grid__add-plus" aria-hidden="true">
            +
          </span>
          <SearchableProjectDropdown
            key={addKey}
            value={null}
            minSearchLength={1}
            placeholder="Type a job number or name to add a row…"
            onChange={(id, option) => {
              if (!option) return;
              onAddProject(option);
              setAddKey((k) => k + 1);
            }}
          />
          <span className="timesheet-grid__add-hint">
            {groups.length === 0 ? 'Pick a project, then type hours into its cells.' : ''}
          </span>
        </div>
      )}
    </div>
  );
};

export default BaseTimesheetGrid;
